import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * The closed trigger + the popover that drops flush from it, styled as one
 * connected control (design: Date & time picking states). The focused trigger
 * keeps the accent border + focus ring and rounds only its top corners; the
 * popover shares the accent border, drops its top border and rounds only its
 * bottom corners.
 */

const ACCENT = '#2f6fdb';

interface Props {
  icon: React.ReactNode;
  /** Formatted value, or empty for the placeholder. */
  displayText?: string;
  placeholder?: string;
  /** Renders the popover body (calendar / wheel). Receives a close callback. */
  children: (close: () => void) => React.ReactNode;
  disabled?: boolean;
  id?: string;
}

const FieldPopover: React.FC<Props> = ({
  icon,
  displayText,
  placeholder = 'Select…',
  children,
  disabled,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<'below' | 'above'>('below');
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The popover lives inside the form's own scroll container (under a
  // sticky action bar and a clipped card), so it is placed where it can be
  // seen: below the trigger when the visible part of the scroller has room,
  // above it when that is where the room is, and otherwise the trigger is
  // scrolled up until the panel fits below it. It is measured again while
  // open, on scroll and on resize (a rotation), and when neither side can
  // hold the whole panel the panel scrolls inside the larger side.
  useLayoutEffect(() => {
    if (!open) return;
    const root = rootRef.current;
    if (!root) return;
    let scroller: HTMLElement | null = root.parentElement;
    while (scroller && scroller !== document.body) {
      const { overflowY } = getComputedStyle(scroller);
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      scroller = scroller.parentElement;
    }
    const inScroller = !!scroller && scroller !== document.body;
    let nudged = false;
    const place = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const host = inScroller ? scroller! : document;
      // a header pinned to the scroller's top edge and the action bar pinned
      // to its bottom edge both cover part of the scrollport
      const pinnedTop = host.querySelector<HTMLElement>('[data-fill-pinned-top]');
      const stickyBar = host.querySelector<HTMLElement>('[data-fill-action-bar]');
      const scrollRect = inScroller ? scroller!.getBoundingClientRect() : null;
      const viewTop = Math.max(scrollRect ? scrollRect.top : 0, pinnedTop ? pinnedTop.getBoundingClientRect().bottom : 0);
      const viewBottom = (scrollRect ? scrollRect.bottom : window.innerHeight) - (stickyBar ? stickyBar.getBoundingClientRect().height : 0);
      const trigger = root.getBoundingClientRect();
      const needed = panel.scrollHeight + 12;
      const roomBelow = viewBottom - trigger.bottom;
      const roomAbove = trigger.top - viewTop;
      if (roomBelow >= needed) {
        setPlacement('below');
        setMaxHeight(undefined);
        return;
      }
      if (roomAbove >= needed) {
        setPlacement('above');
        setMaxHeight(undefined);
        return;
      }
      // neither side fits whole: once, scroll the trigger up to make room
      // below, but never past the top of the scrollport, and cap the panel
      // to the room that move will leave; after that, take the larger side
      // and let the panel scroll inside it
      if (!nudged && inScroller) {
        nudged = true;
        // only as far as the trigger stays visible AND the scroller can actually go
        const range = scroller!.scrollHeight - scroller!.clientHeight - scroller!.scrollTop;
        const move = Math.max(0, Math.min(needed - roomBelow, roomAbove - 8, range));
        if (move > 0) {
          setPlacement('below');
          setMaxHeight(Math.max(0, Math.floor(roomBelow + move - 12)));
          scroller!.scrollBy({ top: move, behavior: 'smooth' });
          return;
        }
      }
      const above = roomAbove > roomBelow;
      setPlacement(above ? 'above' : 'below');
      // never taller than the room: a clipped edge cannot be scrolled to
      setMaxHeight(Math.max(0, Math.floor((above ? roomAbove : roomBelow) - 12)));
    };
    place();
    const target: EventTarget = inScroller ? scroller! : window;
    target.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
    return () => {
      target.removeEventListener('scroll', place);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const above = open && placement === 'above';
  const triggerStyle: React.CSSProperties = open
    ? {
        border: `1.5px solid ${ACCENT}`,
        borderRadius: above ? '0 0 10px 10px' : '10px 10px 0 0',
        boxShadow: '0 0 0 3px rgba(47,111,219,.12)',
      }
    : {
        border: '1px solid var(--forms-border,#dfe3e9)',
        borderRadius: 'var(--forms-input-radius,9px)',
      };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className="flex w-full items-center gap-[10px] bg-white px-[13px] py-[11px] text-left disabled:opacity-60"
        style={triggerStyle}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span
          className="flex-none"
          style={{ color: open ? ACCENT : '#9aa3b0', display: 'inline-flex' }}
        >
          {icon}
        </span>
        <span
          className="flex-1 truncate text-[14px]"
          style={{ color: displayText ? '#1f2a3d' : '#aab2bd' }}
        >
          {displayText || placeholder}
        </span>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute left-0 right-0 z-[50] bg-white"
          style={
            above
              ? {
                  bottom: '100%',
                  border: `1.5px solid ${ACCENT}`,
                  borderBottom: 'none',
                  borderRadius: '12px 12px 0 0',
                  boxShadow: '0 -24px 48px -22px rgba(16,24,40,.4)',
                  overflow: maxHeight ? 'auto' : 'hidden',
                  maxHeight,
                }
              : {
                  top: '100%',
                  border: `1.5px solid ${ACCENT}`,
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  boxShadow: '0 24px 48px -22px rgba(16,24,40,.4)',
                  overflow: maxHeight ? 'auto' : 'hidden',
                  maxHeight,
                }
          }
          role="dialog"
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
};

export default FieldPopover;
